
import { Facet } from './Facet';

/**
 * @public
 */
export type Key<TKey extends object> = string & {
  type: TKey;
};

/**
 * Represents types of key parts.
 * @public
 */
export const enum KeyPartType {
  string = 'string',
  number = 'number',
  boolean = 'boolean',
  subKey = 'subKey'
}

/**
 * Represents options for a key part.
 * In general, use one of the following functions to generate options for a key part:
 * `stringPart()`
 * `numberPart()`
 * `booleanPart()`
 * `subKeyPart()`
 * @public
 */
export interface IKeyPartOptions<TValue> {
  serialize(part: TValue): {} | undefined;
  deserialize(value: {} | undefined): TValue;
}

/**
 * @public
 */
export type IKeyOptions<TKey> = {
  [K in keyof TKey]: IKeyPartOptions<TKey[K]>;
};

/**
 * @public
 */
export interface IStringPartOptions {
  shouldNormalizeCasing?: boolean;
  shouldNormalizeToEmpty?: boolean;
}

const DEFAULT_STRING_PART_OPTIONS = {};

/**
 * Defines a new string part for a key.
 * @param normalizeCasing - Whether or not the given part should be normalized to upper-case.
 * @public
 */
export function stringPart(options: IStringPartOptions = DEFAULT_STRING_PART_OPTIONS): IKeyPartOptions<string> {
  const {
    shouldNormalizeCasing,
    shouldNormalizeToEmpty
  } = options;

  const inner = shouldNormalizeCasing ? normalizeCasing : (part: string) => part;
  const outer = shouldNormalizeToEmpty ? ((part: string) => normalizeToEmpty(inner(part))) : inner;

  return {
    serialize: outer,
    deserialize: (value: string) => value
  };
}

/**
 * Defines a new number part for a key.
 * @public
 */
export function numberPart(): IKeyPartOptions<number> {
  return {
    serialize: (part: number) => part,
    deserialize: (value: number) => value
  };
}

/**
 * Defines a new boolean part for a key.
 * @public
 */
export function booleanPart(): IKeyPartOptions<boolean> {
  return {
    serialize: (part: boolean) => part,
    deserialize: (value: boolean) => value
  };
}

/**
 * Defines a new sub-key part for a key.
 * @public
 */
export function subKeyPart<TSubKey extends object>(keyFacet: KeyFacet<TSubKey>): IKeyPartOptions<TSubKey> {
  return {
    serialize: (part: TSubKey) => {
      return part ? keyFacet.serialize(part) : undefined;
    },
    deserialize: (value: Key<TSubKey> | undefined) => {
      return value ? keyFacet.deserialize(value) : undefined!;
    }
  };
}

/**
 * Defines a form for a key in an item cache and provides operations
 * to convert keys into and out of a string representation.
 *
 * `KeyFacet` declarations are also `Facet` declarations and can be used as such.
 *
 * `KeyFacet` instances should only be declared as module-level constants, or used in code
 * which only runs once within an application. Otherwise there is the potential for a memory leak,
 * as `KeyFacet.get()` maintains a reference to every created instance.
 * @public
 */
export class KeyFacet<TKey extends object> extends Facet<Readonly<TKey>> {
  /**
   * Mapping of every created key facet by its identifier.
   */
  private static _keyFacetsByPrefix: {
    [id: string]: KeyFacet<{}>;
  } = {};

  /**
   * The options for serializing and deserializing the key parts.
   */
  private readonly _options: IKeyOptions<TKey>;
  /**
   * Deterministic order of the key parts.
   * This does not have to be the same across runtime sessions, just within a single runtime session.
   */
  private readonly _partKeys: (keyof TKey)[];

  /**
   * The prefix used to quickly identify this key facet.
   */
  private _prefix: string;

  /**
   * Gets the key facet which is responsible for a given key.
   */
  public static get<TKey extends object>(key: Key<TKey>): KeyFacet<TKey>;
  public static get(key: string): KeyFacet<{}>;
  public static get(key: string): KeyFacet<{}> {
    if (!key) {
      throw new Error('Unable to determine the KeyFacet for an empty or undefined key.');
    }

    const [prefix] = key.split(':', 1);

    const keyFacet = this._keyFacetsByPrefix[prefix];

    if (!keyFacet) {
      const id = JSON.parse(prefix.slice(1));

      throw new Error(
        `Unable to determine the KeyFacet for a key with prefix '${id}'. ` +
        'Only use key values created by invoking [KeyFacet].serialize(___) when storing or fetching items in the Item Cache. ' +
        'This error will typically occur if multiple versions of the @ms/items-view package have been bundled into the application.');
    }

    return keyFacet;
  }

  /**
   * Creates an instance of CacheKey.
   * The `name` is used for the key prefix.
   */
  constructor(name: string, options: IKeyOptions<TKey>) {
    super(name);

    this._options = options;
    this._partKeys = <(keyof TKey)[]>Object.keys(options);

    const prefix = this._prefix = `{${JSON.stringify(this.id)}`;

    // KeyFacet instances *should* be declared as module-level constants in code.
    // Thus it should not leak memory to hold onto references to each one ever created.
    // tslint:disable-next-line:no-any
    KeyFacet._keyFacetsByPrefix[prefix] = this as any; // TODO File a bug on TypeScript.
  }

  /**
   * Determines whether this key definition matches a given serialized key.
   */
  public matches(key: string | Key<TKey> | undefined): key is Key<TKey> {
    if (!key) {
      return false;
    }

    const prefix = this._prefix;

    return key.slice(0, prefix.length) === prefix;
  }

  /**
   * Serializes key parts into a string.
   * The output is to be treated as opaque, and only parsed by `matches` or `deserialize`.
   */
  public serialize(value: TKey): Key<TKey> {
    // TODO Memoize this for performance using WeakMap.

    // Use JSON to handle escaping, while dealing with strings, booleans, sub-objects,
    // and undefined.
    // JSON will translate undefined into null, but it can be converted back.
    return JSON.stringify({
      [this.id]: this._partKeys.map((key: keyof TKey) => {
        const options = this._options[key];

        const part = value[key];

        return options.serialize(part);
      })
    }) as Key<TKey>;
  }

  /**
   * Deserializes a key for its data values.
   * In general, avoid code paths which require key parsing.
   * Instead, attach the unserialized key parts to stored objects to retrieve them directly.
   */
  public deserialize(key: Key<TKey>): TKey {
    const raw = JSON.parse(key);

    const rawParts = raw[this.id];

    if (!rawParts) {
      throw new Error(`Input '${key}' does not match key '${this.id}'.`);
    }

    const parts = {} as TKey;

    for (let i = 0; i < this._partKeys.length; i++) {
      const partKey = this._partKeys[i];
      const rawValue = rawParts[i];
      const options = this._options[partKey];

      let value = options.deserialize(rawValue);

      // JSON translates undefined into null, so convert it back.
      parts[partKey] = value === null ?
        undefined! :
        value;
    }

    return parts;
  }
}

function normalizeCasing(value: string): string {
  return value ? `${value}`.toUpperCase() : value;
}

function normalizeToEmpty(value: string): string {
  return value || '';
}
